import { Link } from "react-router-dom";
import { ArrowUpRight, Inbox, AlertTriangle, RefreshCw } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/* ------------------------------------------------------------------
   Drift building blocks (2026).
   Surface ladder: background → card → secondary. Hairlines, soft
   radii, one accent. Status is always text + dot, never colour alone.
   ------------------------------------------------------------------ */

export const PageHeader = ({ eyebrow, title, subtitle, actions, testid, className }) => (
  <div className={cn("mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between", className)} data-testid={testid}>
    <div className="min-w-0">
      {eyebrow && <p className="eyebrow">{eyebrow}</p>}
      <h1 className="mt-1 font-display text-2xl font-medium tracking-[-0.02em] sm:text-3xl">{title}</h1>
      {subtitle && <p className="mt-1.5 text-[13.5px] text-muted-foreground">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
  </div>
);

export const Panel = ({ title, subtitle, action, children, className, bodyClass, testid, as: As = "section" }) => (
  <As className={cn("shadow-panel min-w-0 rounded-xl bg-card", className)} data-testid={testid}>
    {(title || action) && (
      <header className="flex items-center justify-between gap-3 border-b border-border/70 px-5 py-3.5">
        <div className="min-w-0">
          <h2 className="truncate text-[15px] font-medium tracking-[-0.01em]">{title}</h2>
          {subtitle && <p className="mt-0.5 text-[12.5px] text-muted-foreground">{subtitle}</p>}
        </div>
        {action}
      </header>
    )}
    <div className={cn("p-5", bodyClass)}>{children}</div>
  </As>
);

/* Tone → dot colour. Numbers stay in the foreground; the dot carries the signal. */
const TONE_DOT = { default: null, warn: "bg-warn-dot", danger: "bg-danger-dot", brand: "bg-active-dot", ok: "bg-ok-dot", info: "bg-info-dot" };

export const KpiTile = ({ label, value, to, tone = "default", hint, icon: Icon, testid, className, flat = false }) => {
  const dot = TONE_DOT[tone];
  const inner = (
    <>
      <div className="flex items-start justify-between gap-3">
        <p className="flex items-center gap-2 text-[13px] text-muted-foreground">
          {dot && <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", dot)} aria-hidden />}
          {label}
        </p>
        {Icon ? <Icon className="h-4 w-4 text-muted-foreground/70" strokeWidth={1.75} aria-hidden /> : to ? <ArrowUpRight className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity duration-150 group-hover:opacity-100" strokeWidth={1.75} aria-hidden /> : null}
      </div>
      <p className="t-num mt-3 text-3xl leading-none sm:text-[2.25rem]">{value}</p>
      {hint && <p className="t-mono mt-2">{hint}</p>}
    </>
  );
  const cls = cn(
    "group block min-w-0 p-4 transition-[background-color,box-shadow] duration-200 ease-premium",
    flat ? "rounded-none" : "shadow-panel rounded-xl bg-card",
    to && (flat ? "hover:bg-accent/35" : "hover:bg-accent/30"),
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    className
  );
  return to ? <Link to={to} className={cls} data-testid={testid}>{inner}</Link> : <div className={cls} data-testid={testid}>{inner}</div>;
};

/* A row of KPIs inside one panel. The 1px grid gap over a border-coloured surface draws the hairlines. */
export const KpiStrip = ({ children, className, testid, cols = "grid-cols-2 md:grid-cols-3 xl:grid-cols-6" }) => (
  <div className={cn("shadow-panel grid gap-px overflow-hidden rounded-xl bg-border/70 [&>*]:bg-card", cols, className)} data-testid={testid}>
    {children}
  </div>
);

export const EmptyState = ({ icon: Icon = Inbox, title, text, action, testid, className, compact = false }) => (
  <div className={cn("flex flex-col items-center justify-center rounded-xl bg-secondary/50 px-6 text-center", compact ? "py-8" : "py-14", className)} data-testid={testid}>
    <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-card shadow-panel"><Icon className="h-[18px] w-[18px] text-muted-foreground" strokeWidth={1.75} aria-hidden /></span>
    <p className="mt-4 text-[15px] font-medium">{title}</p>
    {text && <p className="mt-1 max-w-sm text-[13.5px] leading-relaxed text-muted-foreground">{text}</p>}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

export const ErrorState = ({ title = "Noko gjekk gale", text = "Sjekk tilkoplinga og prøv igjen.", onRetry, testid = "drift-error-state" }) => (
  <div className="flex flex-col items-start gap-3 rounded-xl p-4 tone-danger ring-1 sm:flex-row sm:items-center" role="alert" data-testid={testid}>
    <AlertTriangle className="h-4 w-4 shrink-0" strokeWidth={1.75} aria-hidden />
    <div className="min-w-0 flex-1">
      <p className="text-[14px] font-medium">{title}</p>
      <p className="text-[13px] opacity-80">{text}</p>
    </div>
    {onRetry && <Button variant="outline" size="sm" className="rounded-lg bg-card" onClick={onRetry} data-testid={`${testid}-retry`}><RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Prøv igjen</Button>}
  </div>
);

export const LoadingRows = ({ n = 5, className, header = false }) => (
  <div className={cn("shadow-panel overflow-hidden rounded-xl bg-card", className)} data-testid="loading-rows" aria-busy="true" aria-label="Lastar">
    {header && <div className="flex items-center gap-3 border-b border-border/70 px-4 py-3"><Skeleton className="h-4 w-32 rounded-md bg-secondary" /><Skeleton className="ml-auto h-4 w-16 rounded-md bg-secondary" /></div>}
    {Array.from({ length: n }).map((_, i) => (
      <div key={i} className="flex items-center gap-4 border-b border-border/60 px-4 py-3.5 last:border-b-0">
        <Skeleton className="h-4 w-16 rounded-md bg-secondary" />
        <div className="flex-1 space-y-1.5">
          <Skeleton className="h-4 rounded-md bg-secondary" style={{ width: `${44 + ((i * 17) % 40)}%` }} />
          <Skeleton className="h-3 w-1/3 rounded-md bg-secondary" />
        </div>
        <Skeleton className="hidden h-5 w-20 rounded-full bg-secondary sm:block" />
      </div>
    ))}
  </div>
);

export const Field = ({ label, children, className, hint, htmlFor, error }) => (
  <div className={cn("space-y-1.5", className)}>
    <label htmlFor={htmlFor} className="block text-[12.5px] font-medium text-muted-foreground">{label}</label>
    {children}
    {hint && !error && <p className="text-[11.5px] text-muted-foreground">{hint}</p>}
    {error && <p className="text-[11.5px] text-danger">{error}</p>}
  </div>
);

export const KV = ({ k, v, mono = false, className }) => (
  <div className={cn("flex items-start justify-between gap-4 py-2 text-[13.5px]", className)}>
    <span className="text-muted-foreground">{k}</span>
    <span className={cn("text-right", mono && "font-mono text-[13px] tabular")}>{v ?? "–"}</span>
  </div>
);

export const Avatar = ({ name, initials: ini, size = "md", className, tone = "dark" }) => {
  const sz = { xs: "h-5 w-5 text-[9px]", sm: "h-6 w-6 text-[10px]", md: "h-8 w-8 text-[11.5px]", lg: "h-10 w-10 text-[13px]" }[size];
  const txt = ini || (name || "?").split(" ").filter(Boolean).map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const look = tone === "dark" ? "bg-foreground text-background" : "bg-secondary text-foreground ring-1 ring-border/70";
  return <span className={cn("inline-flex shrink-0 items-center justify-center rounded-lg font-display font-semibold", look, sz, className)} title={name} aria-label={name}>{txt}</span>;
};

/* Segmented filter: one row of options, the active one lifted onto a card. */
export const Segmented = ({ options, value, onChange, testid, className, size = "md" }) => (
  <div role="tablist" className={cn("inline-flex max-w-full items-center gap-0.5 overflow-x-auto rounded-xl bg-secondary p-1 scrollbar-none", className)} data-testid={testid}>
    {options.map(([k, label, count]) => {
      const on = value === k;
      return (
        <button
          key={k}
          type="button"
          role="tab"
          aria-selected={on}
          onClick={() => onChange(k)}
          data-testid={testid ? `${testid}-${k}` : undefined}
          className={cn(
            "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg font-medium transition-[background-color,color,box-shadow] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            size === "sm" ? "h-7 px-2.5 text-[12.5px]" : "h-8 px-3 text-[13px]",
            on ? "bg-card text-foreground shadow-[0_1px_0_hsl(var(--border)),0_1px_2px_hsla(240,6%,8%,0.06)]" : "text-muted-foreground hover:text-foreground"
          )}
        >
          {label}
          {count !== undefined && <span className={cn("tabular text-[11.5px]", on ? "text-muted-foreground" : "text-muted-foreground/70")}>{count}</span>}
        </button>
      );
    })}
  </div>
);

/* Table wrapper and header cell with the shared look. */
export const TableShell = ({ children, className, testid }) => (
  <div className={cn("shadow-panel overflow-hidden rounded-xl bg-card", className)} data-testid={testid}>
    <div className="overflow-x-auto">
      <table className="w-full text-[13.5px] leading-5">{children}</table>
    </div>
  </div>
);
export const Th = ({ children, className, align = "left" }) => (
  <th className={cn("sticky top-0 z-[1] whitespace-nowrap bg-card/95 px-4 py-2.5 text-[12px] font-medium text-muted-foreground backdrop-blur", align === "right" && "text-right", className)}>{children}</th>
);
export const trCls = "cursor-pointer transition-[background-color] duration-150 hover:bg-accent/35 focus-visible:outline-none focus-visible:bg-accent/35";
